/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-17 12:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { SubmissionError } from 'redux-form';
import { capitalizeFirstLetter, prettyText } from './index';
import { MERGE_VALUES_TYPES } from 'src/constants/merge-values';
import { myXss } from 'src/helpers';


/**
 * Build data for merge value types
 *
 * @return {Object} merge value types list
 */
export const getAvailableTypes = (options: Array<Object>, locale: string, i18n: Object) => {
  const optionTypes = _.map([...MERGE_VALUES_TYPES], function (value) {
    return {
      ...value,
      label: i18n.translate(value.label)
    };
  });

  options.map(item => {
    if (item) {
      optionTypes.splice(3, 0, {
        value: `opt_${item.name}`,
        label: `Option: ${capitalizeFirstLetter(item.originalName, locale)}`,
        type: 'option'
      });
    }
    return '';
  });

  return optionTypes;
};

/**
 * Build data type for merge value type
 *
 * @return {Object} merge value type
 */
export const getTypeInformation = (
  typeValue: string,
  options: Array<{ name: string, originalName: string }>,
  locale: string,
  i18n: Object
) => {
  if (!typeValue) return undefined;

  const type = _.find([...MERGE_VALUES_TYPES], item => item.value === typeValue);

  // Product Option
  if (_.isEmpty(type)) {
    // Get original name
    const name = typeValue ? typeValue.replace('opt_', '') : typeValue;
    const option = _.find(options, { name }) || {};
    const originalName = _.get(option, 'originalName', name);

    return {
      value: typeValue,
      label: `Option: ${capitalizeFirstLetter(originalName, locale)}`
    };
  } else {
    return {
      ...type,
      label: i18n.translate(type.label)
    };
  }
};

/**
 * Build data for merge values list with type
 *
 * @param  {Object} type type of merge value
 * @param  {Object} state state of store
 *
 * @return {Object} value list of types list
 */
export const getValuesOfType = (type: string, state: Object) => {
  if (_.isEmpty(type)) {
    return [];
  }

  switch (type) {
    case 'product_type': {
      const productTypes = _.get(state.filterData.filterData, 'productTypes', []);

      return productTypes;
    }
    case 'vendor': {
      const vendors = _.get(state.filterData.filterData, 'vendors', []);
      return vendors;
    }
    case 'tag': {
      const tags = _.get(state.filterData.filterData, 'tags', []);

      return tags;
    }
    default: {
      // Options
      if (type.match(/opt_.*/g)) {
        const optionsName = type.substring(4);
        const options = _.get(state.filterData.filterData, optionsName, []);

        return options;
      }
      return [];
    }
  }
};

/**
 * Build initial data for one merge value
 *
 * @param  {Object} rule merge value from API
 *
 * @return {Object} initialValue
 */
export const buildInitialRule = (rule: Object, type: string) => {
  // Has value
  if (!_.isEmpty(rule)) {
    const { fromValues = [] } = rule;
    const initialFromValues = fromValues.map(fromValue => {
      return { value: fromValue, label: fromValue };
    });
    return { ...rule, fromValues: initialFromValues };
  }

  // New value
  return {
    status: 'active',
    fromValues: [],
    toValue: '',
    type
  };
};

/**
 * Validate rule form data before save to merge value
 *
 * @param  {Object}   rule data to check validate
 * @param  {Object}   filterOptions filter option list of filter tree
 *
 * @return {boolean/error} true if well validated, throw error if have
 */
export const validateRule = (rule: Object, index: number, rules: Array<Object>, i18n: Object) => {
  let ruleError = {};

  // From value
  if (_.isEmpty(rule.fromValues)) {
    ruleError.fromValues = i18n.translate('AdminHTML.MergeValues.Helper.validateSaveOption_Error2');
  } else {
    rule.fromValues.forEach((item) => {
      console.log(item, item.value, myXss.process(item.value));
      if(item.value !== myXss.process(item.value)) {
        ruleError.fromValues = i18n.translate('AdminHTML.Helpers.FormValidationHelper.nonXSS');
      }
    })
  }

  // To value
  if (_.isEmpty(rule.toValue)) {
    ruleError.toValue = i18n.translate('AdminHTML.MergeValues.Helper.validateSaveOption_Error3');
  } else {
    if (/<[^ >]+>/g.test(rule.toValue)) {
      ruleError.toValue = i18n.translate('AdminHTML.MergeValues.Helper.validateSaveOption_Error4');
    } else {
      let numb = 0;
      rules.forEach((item, index1) => {
        if (index !== index1 && item.toValue === rule.toValue) {
          numb += 1;
        }
      });
      if (numb > 0) {
        ruleError.toValue = i18n.translate(
          'AdminHTML.MergeValues.Helper.validateSaveOption_Error5'
        );
      }
    }
  }

  if (!_.isEmpty(ruleError)) {
    throw new SubmissionError(ruleError);
  }

  return true;
};

/**
 * Build data for one merge value
 *
 * @param  {Object} mergeValues merge value from API
 *
 * @return {Object} initialValue
 */
export const buildRule = (rule: Object) => {
  let { id = '', status, fromValues, toValue: toValueForm, type } = rule;
  let result = {
    id,
    status,
    type
  };

  fromValues = fromValues.map(fromValue => {
    return fromValue.value;
  });

  const toValue = prettyText(toValueForm);

  result = { ...result, toValue, fromValues };

  return result;
};
